import type { Middleware } from 'redux';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

import { bootstrapActionTypes, dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { SEED_CUSTOMER_CARD } from './store/actions';
import { checkCustomerSpdRSAA } from './store/customerSpdActionCreators';
import { OPERATION_LIFECYCLE_ACTION_TYPES, logOperationLifecycleEvent } from './utils/operationLifecycleLogger';
import { SEARCH_ATTEMPT_ACTION_TYPES, logSearchAttemptEvent } from './utils/searchAttemptLogger';
import type { CustomerCardState } from './types';

export interface CustomerCardMiddlewareManager {
    middleware: Middleware;
}

type CustomerCardStoreState = {
    customerCard?: CustomerCardState;
};

/**
 * Initializes the customer card activity middleware.
 *
 * Returns a MiddlewareManager that acts as a DealXG fallback seeder:
 *
 * - If the host middleware has already seeded the store on BOOTSTRAP_SUCCESS
 *   (e.g. MV via offer-redux selectors, SCI via its own store), this middleware
 *   is a no-op — the host data takes precedence.
 *
 * - If the store is unseeded at bootstrap (no host seed was dispatched), this
 *   middleware is responsible for fetching buyer/coBuyer from DealXG and
 *   dispatching seedCustomerCard as a fallback.
 *
 * Usage: the host middleware should call init() on bootstrap and route all
 * subsequent Redux actions through middlewareMgr.middleware.
 */
export const init = (): { middlewareMgr: CustomerCardMiddlewareManager } => {
    const middleware: Middleware = (store) => (next) => (action: AnyFSA) => {
        const isOperationLifecycleAction = (OPERATION_LIFECYCLE_ACTION_TYPES as readonly string[]).includes(action.type);

        // BACK/CANCEL_CARD_OPERATION clear or step back the operation before the reducer runs, so
        // this has to be read pre-transition regardless of action type — see
        // logOperationLifecycleEvent's own doc comment for why the same read works for all four.
        const customerCardBeforeTransition = isOperationLifecycleAction
            ? (store.getState() as CustomerCardStoreState).customerCard
            : undefined;

        if (action.type === bootstrapActionTypes.BOOTSTRAP_SUCCESS) {
            const state = store.getState() as CustomerCardStoreState;

            // The host middleware seeds the store synchronously during its own
            // BOOTSTRAP_SUCCESS handler. By the time this middleware runs, the
            // state already reflects the host seed if one was dispatched.
            const isSeededByHost = !!state.customerCard?.buyer;

            if (!isSeededByHost) {
                // TODO: Fetch buyer/coBuyer from DealXG and seed the store.
                // This is the fallback path for hosts that do not seed directly.
                //
                // const bffBaseUrl = getCustomerCardBffBaseUrl();
                // const dealXgData = await fetchCustomerFromDealXg(bffBaseUrl, dealId);
                // store.dispatch(
                //     seedCustomerCard({
                //         buyer: dealXgData.buyer,
                //         coBuyer: dealXgData.coBuyer ?? null,
                //         hasCoBuyer: dealXgData.hasCoBuyer,
                //         initialTab: 'buyer'
                //     })
                // );
            }
        }

        // Run the action first so the slice is populated (buyer/coBuyer CCIDs + dealId)
        // before the CHECK thunk reads them from the store.
        const result = next(action);

        // The activity owns the SPD lifecycle: on host seed, fire the presence CHECK for both
        // participants here rather than making the host dispatch it. Pre-fetch is on by default
        // — hosts can opt out by seeding with `prefetchSpd: false`.
        if (action.type === SEED_CUSTOMER_CARD && action.payload?.prefetchSpd !== false) {
            const state = store.getState();
            if (featureToggleSelectors.isActivitiesCustomerCardEnabled(state) && dealerSelectors.isSpdManagementEnabled(state)) {
                store.dispatch(checkCustomerSpdRSAA() as never);
            }
        }

        // Logged here rather than at each dispatch site so it covers every entry point uniformly —
        // ChangeCustomerCta's startOperation calls and UnifiedCustomerCard's continue/back/cancel
        // calls alike, without either component needing to know about NR.
        if (isOperationLifecycleAction) {
            logOperationLifecycleEvent(action, customerCardBeforeTransition);
        } else if ((SEARCH_ATTEMPT_ACTION_TYPES as readonly string[]).includes(action.type)) {
            // Two separate thunks (consumer search, business search) dispatch these — logged once
            // here instead of duplicating the call in both. Post-transition state, unlike the
            // operation-lifecycle branch above: this needs SEARCH_REQUEST's own attemptCount
            // increment, which next(action) is what applies.
            logSearchAttemptEvent(action, (store.getState() as CustomerCardStoreState).customerCard);
        }

        return result;
    };

    return { middlewareMgr: { middleware } };
};
